/**
 * formatters.ts - Data Formatting and Validation Utilities
 * 
 * This utility module provides functions for formatting user input data and validating
 * common data types like phone numbers, emails, and payment information.
 * 
 * Key Functions:
 * - formatPhoneNumber: Formats phone numbers based on country
 * - isValidEmail: Validates email address format
 * - formatCardNumber: Formats credit card numbers with spacing
 * - formatExpirationDate: Formats card expiration dates (MM/YY)
 * 
 * Features:
 * - Country-specific phone number formatting
 * - Real-time input formatting for better UX
 * - Validation with regex patterns
 * - Input length limits and sanitization
 * 
 * Dependencies: None (pure JavaScript/TypeScript)
 */

// Utility functions for formatting and validation

export const formatPhoneNumber = (value: string, country: string): string => {
  if (country === 'United States') {
    // Remove all non-digits first, then apply formatting
    const digits = value.replace(/\D/g, '');
    
    // Allow complete clearing
    if (digits.length === 0) {
      return '';
    }
    
    // Limit to 10 digits and format based on length
    const limitedDigits = digits.slice(0, 10);
    
    if (digits.length >= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    }
    return limitedDigits;
  } else {
    // For non-US, allow up to 16 digits without formatting
    return value.replace(/\D/g, '').slice(0, 16);
  }
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const formatCardNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Limit to 19 digits maximum
  const limitedDigits = digits.slice(0, 19);
  // Add spaces every 4 digits
  return limitedDigits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

export const formatExpirationDate = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Add slash after 2 digits
  if (digits.length >= 2) {
    return digits.substring(0, 2) + '/' + digits.substring(2, 4);
  }
  return digits;
};
/**
 * Keeps a typed donation amount as dollars and cents: digits plus ONE decimal point, at most 2 decimals,
 * no leading zeros. The custom amount box used to strip every non-digit, so "10.50" became "1050" and
 * the donor was charged 100x what they meant (security finding F04).
 */
export const sanitizeCurrencyInput = (value: string): string => {
  let cleaned = value.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
  }
  cleaned = cleaned.replace(/^0+(?=\d)/, "");
  if (cleaned.startsWith(".")) cleaned = "0" + cleaned;
  return cleaned;
};

/** Parses a sanitized amount to a number rounded to cents; 0 when empty or not a number. */
export const parseCurrencyAmount = (value: string | null | undefined): number => {
  const amount = parseFloat(value || "");
  return isNaN(amount) ? 0 : Math.round(amount * 100) / 100;
};
